import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Plus, Trash2, Hash, Search, X, Eye, EyeOff, Tag } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { can } from "../../utils/access";
import {
  AppButton, ConfirmModal, AppCard, AppCardContent, EmptyState,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import "./AICategories.scss";

/**
 * CategoryManager — reusable manager for one AI category type
 * (learn_tech / mock_tech / mock_role / mock_type).
 *
 * Renders built-in defaults (hide/show toggle) and the institute's custom
 * entries (hide/show + delete), backed by:
 *   GET   /ai/categories/manage?type=<type>
 *   POST  /ai/categories           { type, name }
 *   PATCH /ai/categories/visibility { type, name, hidden }
 *   DELETE /ai/categories/:id
 *
 * Props:
 *   type          category type key (required)
 *   title         section heading
 *   description   helper text under the heading
 *   addPlaceholder placeholder for the add input
 *   showCounts    show per-category question counts (learn_tech only)
 */
const CategoryManager = ({
  type,
  title,
  description,
  addPlaceholder = "e.g. GraphQL, Rust, System Design",
  showCounts = false,
}) => {
  const canManage = can("ai.manage");
  const [defaults, setDefaults] = useState([]);
  const [custom, setCustom] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [togglingKey, setTogglingKey] = useState(null);

  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const showToast = (message, t = "success") => setToast({ show: true, message, type: t });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/ai/categories/manage", { params: { type } });
      setDefaults(data.defaults || []);
      setCustom(data.custom || []);
    } catch {
      showToast("Failed to load categories", "error");
    }
    setLoading(false);
  }, [type]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e?.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await apiClient.post("/ai/categories", { type, name });
      setNewName("");
      showToast(`"${name}" added`, "success");
      fetchData();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to add category", "error");
    }
    setCreating(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/ai/categories/${deleteTarget._id}?force=1`);
      showToast(`"${deleteTarget.name}" deleted`, "success");
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to delete category", "error");
    }
    setDeleting(false);
  };

  const handleToggleHidden = async (item) => {
    const nextHidden = !item.hidden;
    setTogglingKey(item.name.toLowerCase());
    try {
      await apiClient.patch("/ai/categories/visibility", {
        type,
        name: item.name,
        hidden: nextHidden,
      });
      // Optimistic local update — cheaper than a full refetch.
      const patch = (arr) =>
        arr.map((c) =>
          c.name.toLowerCase() === item.name.toLowerCase() ? { ...c, hidden: nextHidden } : c
        );
      setDefaults((d) => patch(d));
      setCustom((c) => patch(c));
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to update visibility", "error");
    }
    setTogglingKey(null);
  };

  const q = search.trim().toLowerCase();
  const filteredCustom = useMemo(
    () => (q ? custom.filter((c) => c.name.toLowerCase().includes(q)) : custom),
    [custom, q]
  );
  const filteredDefaults = useMemo(
    () => (q ? defaults.filter((c) => c.name.toLowerCase().includes(q)) : defaults),
    [defaults, q]
  );

  const hiddenCount =
    defaults.filter((d) => d.hidden).length + custom.filter((c) => c.hidden).length;

  const renderCard = (item, isDefault) => {
    const busy = togglingKey === item.name.toLowerCase();
    return (
      <article
        key={isDefault ? `d-${item.name}` : item._id}
        className={`ai-cats-card${isDefault ? " ai-cats-card--default" : ""}${item.hidden ? " ai-cats-card--hidden" : ""}`}
      >
        <div className="ai-cats-card-main">
          <span className="ai-cats-card-name" title={item.name}>{item.name}</span>
          {showCounts && typeof item.questionCount === "number" && (
            <span className="ai-cats-card-count">
              <Hash size={12} /> {item.questionCount} question{item.questionCount === 1 ? "" : "s"}
            </span>
          )}
        </div>
        {canManage && (
        <div className="ai-cats-card-actions">
          <button
            type="button"
            className={`ai-cats-icon-btn${item.hidden ? " ai-cats-icon-btn--muted" : ""}`}
            onClick={() => handleToggleHidden(item)}
            disabled={busy}
            title={item.hidden ? "Hidden from students — click to show" : "Visible to students — click to hide"}
            aria-label={item.hidden ? `Show ${item.name} to students` : `Hide ${item.name} from students`}
          >
            {item.hidden ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
          {!isDefault && (
            <button
              type="button"
              className="ai-cats-icon-btn ai-cats-icon-btn--danger"
              onClick={() => setDeleteTarget(item)}
              title="Delete category"
              aria-label={`Delete ${item.name}`}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
        )}
      </article>
    );
  };

  return (
    <div className="ai-cats-page">
      {(title || description) && (
        <div className="ai-cats-manager-head">
          {title && <h3 className="ai-cats-section-title">{title}</h3>}
          {description && <p className="ai-cats-add-hint">{description}</p>}
        </div>
      )}

      {/* Stats + search */}
      <div className="ai-cats-toolbar">
        <div className="ai-cats-stats">
          <div className="ai-cats-stat">
            <span className="ai-cats-stat-value">{loading ? "—" : custom.length}</span>
            <span className="ai-cats-stat-label">Custom</span>
          </div>
          <div className="ai-cats-stat">
            <span className="ai-cats-stat-value">{loading ? "—" : defaults.length}</span>
            <span className="ai-cats-stat-label">Built-in</span>
          </div>
          <div className="ai-cats-stat">
            <span className="ai-cats-stat-value">{loading ? "—" : hiddenCount}</span>
            <span className="ai-cats-stat-label">Hidden</span>
          </div>
        </div>

        <div className="ai-cats-search">
          <Search size={16} className="ai-cats-search-icon" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
          />
          {search && (
            <button
              type="button"
              className="ai-cats-search-clear"
              onClick={() => setSearch("")}
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Quick add */}
      {canManage && (
      <AppCard className="ai-cats-add-card">
        <AppCardContent>
          <form className="ai-cats-add-form" onSubmit={handleCreate}>
            <div className="ai-cats-add-row">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={addPlaceholder}
                maxLength={80}
                disabled={creating}
                className="ai-cats-add-input"
              />
              <AppButton type="submit" variant="default" disabled={creating || !newName.trim()}>
                <Plus size={16} /> {creating ? "Adding..." : "Add"}
              </AppButton>
            </div>
          </form>
        </AppCardContent>
      </AppCard>
      )}

      {/* Custom entries */}
      <section className="ai-cats-section">
        <h4 className="ai-cats-section-title">Your entries</h4>
        {loading ? (
          <div className="ai-cats-skeletons">
            <div className="ai-cats-skeleton" />
            <div className="ai-cats-skeleton" />
            <div className="ai-cats-skeleton" />
          </div>
        ) : filteredCustom.length === 0 ? (
          <EmptyState
            icon={<Tag size={32} />}
            title={search ? "No matching entries" : "No custom entries yet"}
            description={
              search ? "Try a different search term." : "Add your first entry above."
            }
          />
        ) : (
          <div className="ai-cats-grid">
            {filteredCustom.map((c) => renderCard(c, false))}
          </div>
        )}
      </section>

      {/* Built-in defaults */}
      {filteredDefaults.length > 0 && (
        <section className="ai-cats-section">
          <h4 className="ai-cats-section-title">
            Built-in defaults
            <span className="ai-cats-section-hint">Hide any you don't want students to see</span>
          </h4>
          <div className="ai-cats-grid">
            {filteredDefaults.map((c) => renderCard(c, true))}
          </div>
        </section>
      )}

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete entry?"
        message={
          deleteTarget?.questionCount > 0
            ? `"${deleteTarget?.name}" is used by ${deleteTarget.questionCount} question(s). The questions keep their tag, but the entry is removed from your list.`
            : `Remove "${deleteTarget?.name}" from your list?`
        }
        confirmText={deleting ? "Deleting..." : "Delete"}
        variant="danger"
        loading={deleting}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default CategoryManager;
